import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, TrendingUp } from "lucide-react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export default function CaseStudiesPage() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/api/case-studies?published_only=true`)
      .then(r => setCases(r.data))
      .catch(() => setCases([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div data-testid="case-studies-page">
      {/* Hero */}
      <section className="pt-32 pb-20 md:pt-40 md:pb-28 bg-navy-900">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold text-gold-500 mb-4 font-sans">Track Record</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-white font-light tracking-tight max-w-3xl">
            Results that speak for themselves.
          </h1>
          <p className="text-white/60 font-sans text-base md:text-lg mt-6 max-w-2xl leading-relaxed">
            Explore how Capstone has helped enterprises across sectors achieve their financial objectives.
          </p>
        </div>
      </section>

      {/* Case Studies */}
      <section className="py-24 md:py-32">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          {loading ? (
            <div className="text-center py-20"><div className="w-8 h-8 border-2 border-gold-500 border-t-transparent rounded-full animate-spin mx-auto" /></div>
          ) : cases.length === 0 ? (
            <p className="text-gray-400 font-sans text-center py-20">No case studies published yet.</p>
          ) : (
            <div className="space-y-0 border border-gray-200">
              {cases.map((cs, i) => (
                <div
                  key={cs.id}
                  data-testid={`case-study-${i}`}
                  className={`p-10 md:p-12 ${i < cases.length - 1 ? 'border-b border-gray-200' : ''}`}
                >
                  {/* Header */}
                  <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-8">
                    <div>
                      <span className="text-xs font-sans text-gold-500 uppercase tracking-[0.15em]">{cs.sector}</span>
                      <h2 className="font-serif text-2xl md:text-3xl text-navy-900 mt-2">{cs.title}</h2>
                      <p className="text-gray-400 font-sans text-sm mt-1">{cs.client_name}</p>
                    </div>
                    {cs.metrics && cs.metrics.length > 0 && (
                      <div className="flex flex-wrap gap-6">
                        {cs.metrics.map((m) => (
                          <div key={m.label} className="text-right">
                            <p className="font-serif text-2xl text-gold-500">{m.value}</p>
                            <p className="text-gray-400 text-xs font-sans uppercase tracking-[0.1em]">{m.label}</p>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Problem / Approach / Outcome */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                    <div>
                      <h3 className="font-sans text-xs uppercase tracking-[0.2em] text-gold-500 font-semibold mb-3">The Challenge</h3>
                      <p className="text-gray-600 font-sans text-sm leading-relaxed">{cs.problem}</p>
                    </div>
                    <div>
                      <h3 className="font-sans text-xs uppercase tracking-[0.2em] text-gold-500 font-semibold mb-3">Our Approach</h3>
                      <p className="text-gray-600 font-sans text-sm leading-relaxed">{cs.approach}</p>
                    </div>
                    <div>
                      <h3 className="font-sans text-xs uppercase tracking-[0.2em] text-gold-500 font-semibold mb-3">The Outcome</h3>
                      <p className="text-gray-600 font-sans text-sm leading-relaxed">{cs.outcome}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-24 md:py-32 bg-navy-900 text-center">
        <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-16">
          <TrendingUp className="w-10 h-10 text-gold-500 mx-auto mb-6" strokeWidth={1.5} />
          <h2 className="font-serif text-4xl sm:text-5xl text-white font-light tracking-tight mb-6">
            Your success story starts here.
          </h2>
          <p className="text-white/60 font-sans mb-10 max-w-xl mx-auto">
            Let's discuss how Capstone can help your enterprise achieve transformative results.
          </p>
          <Link
            to="/contact"
            data-testid="case-studies-cta-button"
            className="inline-flex items-center gap-2 bg-gold-500 text-navy-900 px-8 py-4 rounded-sm font-sans text-sm font-medium hover:bg-gold-400 transition-colors duration-300"
          >
            Start a Conversation <ArrowUpRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
}
